import { useEffect, useRef, useState } from "react";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { ICategories, IDepartments, IProduct } from "redux/reducers/models/product";
import { defaultBrowseFilter, IBrowseFilter } from "components/folders/productBrowseFilters";
import {
  appendUniqueProducts,
  buildSelectAllProductBatch,
  fetchFolderProductSearchPage,
  IFolderProductSearchContext,
  sliceSelectAllBatch,
} from "components/folders/folderProductSearch";

type BrowseDepartment = IDepartments & { cateogries?: ICategories[] };

type Options = {
  open: boolean;
  maxSelections: number;
  includeVariants?: boolean;
  /** Count selected variants toward maxSelections. Product flows count products. */
  countVariants?: boolean;
  onError: (message: string) => void;
};

function countedSelections(products: IProduct[], countVariants: boolean): number {
  if (!countVariants) {
    return products.length;
  }

  return products.reduce((sum, product) => {
    const variantCount = product.variants?.length ?? 0;
    return sum + (variantCount > 0 ? variantCount : 1);
  }, 0);
}

function mergeProductSelections(
  current: IProduct[],
  incomingProducts: IProduct[],
  maxSelections: number,
  countVariants: boolean,
): { next: IProduct[]; truncated: boolean } {
  if (!countVariants) {
    return { next: current, truncated: false };
  }

  const next = [...current];
  let remaining = maxSelections - countedSelections(next, true);
  let truncated = false;

  incomingProducts.forEach(product => {
    const incomingVariants = product.variants ?? [];
    const index = next.findIndex(item => item.id === product.id);

    if (incomingVariants.length === 0) {
      if (index !== -1) {
        return;
      }
      if (remaining <= 0) {
        truncated = true;
        return;
      }
      next.push(product);
      remaining -= 1;
      return;
    }

    const existingVariants = index === -1 ? [] : next[index].variants ?? [];
    const existingIds = new Set(existingVariants.map(variant => variant.id));
    const unseen = incomingVariants.filter(variant => !existingIds.has(variant.id));
    const toAdd = unseen.slice(0, Math.max(remaining, 0));
    if (toAdd.length < unseen.length) {
      truncated = true;
    }
    if (toAdd.length === 0) {
      return;
    }

    const mergedVariants = [...existingVariants, ...toAdd];
    if (index === -1) {
      next.push({ ...product, variants: mergedVariants, variant_count: mergedVariants.length });
    } else {
      next[index] = { ...next[index], variants: mergedVariants, variant_count: mergedVariants.length };
    }
    remaining -= toAdd.length;
  });

  return { next, truncated };
}

/**
 * Shared product search/browse state used by AddFolderProductsModal
 * (Folders edit + Accounts batch sell).
 */
export function useAddFolderProductsPicker(options: Options) {
  const { open, maxSelections, includeVariants = false, countVariants = false, onError } = options;

  const [search, setSearch] = useState("");
  const [browseFilter, setBrowseFilter] = useState<IBrowseFilter>(defaultBrowseFilter);
  const [productsToAdd, setProductsToAdd] = useState<IProduct[]>([]);
  const [productQuantities, setProductQuantities] = useState<Record<number, number>>({});
  const [productSearchResults, setProductSearchResults] = useState<IProduct[] | undefined>(undefined);
  const [productSearchTotalCount, setProductSearchTotalCount] = useState<number | undefined>(undefined);
  const [productSearchPending, setProductSearchPending] = useState(false);
  const [productSearchLoadingMore, setProductSearchLoadingMore] = useState(false);
  const [productSearchSelectAllPending, setProductSearchSelectAllPending] = useState(false);
  const [departments, setDepartments] = useState<BrowseDepartment[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);

  const browseFilterRef = useRef(browseFilter);
  const prevOpenRef = useRef(false);
  const productSearchCancelTokenRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);

  function getProduct(params: Record<string, unknown>, useGlobalLoader: boolean, cancelToken?: CancelToken) {
    return GetProduct(
      {
        ...params,
        ...(includeVariants
          ? { variants: true, extended_variants: true, inventory: true }
          : {}),
      },
      useGlobalLoader,
      cancelToken,
    );
  }

  function getProductSearchContext(): IFolderProductSearchContext {
    return { search, browseFilter };
  }

  useEffect(() => {
    if (!open) {
      productSearchCancelTokenRef.current?.cancel();
      setProductSearchPending(false);
      setProductSearchLoadingMore(false);
      prevOpenRef.current = false;
      return;
    }

    const justOpened = !prevOpenRef.current;
    prevOpenRef.current = true;

    if (justOpened) {
      void loadBrowseMetadata();
    }

    const browseChanged =
      browseFilterRef.current.type !== browseFilter.type || browseFilterRef.current.id !== browseFilter.id;
    browseFilterRef.current = browseFilter;
    const delay = browseChanged ? 0 : 1000;

    let mounted = true;
    productSearchCancelTokenRef.current?.cancel();
    const cancelSource = axios.CancelToken.source();
    productSearchCancelTokenRef.current = cancelSource;

    setProductSearchPending(true);
    setProductSearchLoadingMore(false);
    setProductSearchResults(prev => {
      if ((prev?.length ?? 0) > 0) {
        return prev;
      }
      setProductSearchTotalCount(undefined);
      return undefined;
    });

    const runFetch = async () => {
      try {
        const { products, totalCount } = await fetchFolderProductSearchPage(
          getProduct,
          getProductSearchContext(),
          0,
          true,
          cancelSource.token,
          () => onError("Failed to get products"),
        );
        if (mounted) {
          setProductSearchResults(products);
          setProductSearchTotalCount(totalCount);
          setProductSearchPending(false);
          setProductSearchLoadingMore(false);
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        if (mounted) {
          setProductSearchResults([]);
          setProductSearchTotalCount(0);
          setProductSearchPending(false);
          setProductSearchLoadingMore(false);
        }
      }
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (justOpened) {
      void runFetch();
    } else {
      timeoutId = global.setTimeout(() => {
        void runFetch();
      }, delay);
    }

    return () => {
      mounted = false;
      cancelSource.cancel();
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [open, search, browseFilter]);

  async function loadBrowseMetadata() {
    const showLoading = departments.length === 0;
    if (showLoading) {
      setDepartmentsLoading(true);
    }

    try {
      const [departmentRes, countRes] = await Promise.all([
        GetDepartments({ extended: true }, false),
        GetProduct(
          {
            get_count: true,
            limit: 1,
            filters: [{ label: "uncategorized", value: [1] }],
          },
          false,
        ),
      ]);

      if (departmentRes.status === StatusCode.OK) {
        setDepartments((departmentRes.data ?? []).filter(department => department.type === "department"));
      }

      if (countRes.status === StatusCode.OK && countRes.data && !Array.isArray(countRes.data)) {
        const countData = countRes.data as { count?: number };
        setUncategorizedCount(countData.count ?? 0);
      }
    } finally {
      if (showLoading) {
        setDepartmentsLoading(false);
      }
    }
  }

  async function loadMoreProducts() {
    let shouldLoad = false;
    let offset = 0;

    if (
      productSearchLoadingMore ||
      productSearchPending ||
      !productSearchResults ||
      productSearchTotalCount == null ||
      productSearchResults.length >= productSearchTotalCount
    ) {
      return;
    }

    shouldLoad = true;
    offset = productSearchResults.length;
    setProductSearchLoadingMore(true);

    if (!shouldLoad) {
      return;
    }

    try {
      const { products } = await fetchFolderProductSearchPage(
        getProduct,
        getProductSearchContext(),
        offset,
        false,
        undefined,
        () => onError("Failed to get products"),
      );
      setProductSearchResults(prev => appendUniqueProducts(prev ?? [], products));
      setProductSearchLoadingMore(false);
    } catch {
      setProductSearchLoadingMore(false);
    }
  }

  async function selectAllMatchingProducts() {
    const loaded = productSearchResults ?? [];
    const totalCount = productSearchTotalCount ?? loaded.length;
    const remaining = maxSelections - countedSelections(productsToAdd, countVariants);
    const excludeIds = new Set(productsToAdd.map(product => product.id));
    const needsRemoteFetch = remaining > 0 && loaded.length < totalCount;
    const selectAllExceedsLimit = countVariants && totalCount > maxSelections;

    if (selectAllExceedsLimit) {
      onError(`A segment can have up to ${maxSelections} products`);
    }

    if (needsRemoteFetch) {
      setProductSearchSelectAllPending(true);
    }

    try {
      const toAdd = await buildSelectAllProductBatch(
        getProduct,
        getProductSearchContext(),
        loaded,
        totalCount,
        excludeIds,
        remaining,
        () => onError("Failed to get products"),
      );

      const batch = sliceSelectAllBatch(toAdd, productsToAdd, maxSelections);
      if (batch.length > 0 && countVariants) {
        let truncated = false;
        setProductsToAdd(prev => {
          const merged = mergeProductSelections(prev, batch, maxSelections, true);
          truncated = merged.truncated;
          return merged.next;
        });
        if (truncated) {
          onError(`A segment can have up to ${maxSelections} products`);
        }
      } else if (batch.length > 0) {
        setProductsToAdd(prev => [...prev, ...batch]);
        setProductQuantities(prev => {
          const next = { ...prev };
          batch.forEach(product => {
            if (next[product.id] == null) {
              next[product.id] = 1;
            }
          });
          return next;
        });
      }
      setProductSearchSelectAllPending(false);
    } catch {
      setProductSearchSelectAllPending(false);
    }
  }

  function handleSelectProduct(productOrProducts: IProduct | IProduct[]) {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];

    if (countVariants) {
      let truncated = false;
      setProductsToAdd(prev => {
        const merged = mergeProductSelections(prev, products, maxSelections, true);
        truncated = merged.truncated;
        return merged.next;
      });
      if (truncated) {
        onError(`A segment can have up to ${maxSelections} products`);
      }
      return;
    }

    setProductsToAdd(prev => {
      const next = [...prev];
      let remaining = maxSelections - next.length;
      const addedProducts: IProduct[] = [];

      products.forEach(product => {
        const index = next.findIndex(item => item.id === product.id);
        if (index === -1) {
          if (remaining <= 0) {
            return;
          }
          next.push(product);
          addedProducts.push(product);
          remaining -= 1;
          return;
        }

        const existingVariantIds = new Set((next[index].variants ?? []).map(variant => variant.id));
        const mergedVariants = [...(next[index].variants ?? [])];
        (product.variants ?? []).forEach(variant => {
          if (!existingVariantIds.has(variant.id)) {
            mergedVariants.push(variant);
            existingVariantIds.add(variant.id);
          }
        });
        next[index] = { ...next[index], variants: mergedVariants, variant_count: mergedVariants.length };
      });

      if (addedProducts.length > 0) {
        setProductQuantities(quantities => {
          const updated = { ...quantities };
          addedProducts.forEach(product => {
            if (updated[product.id] == null) {
              updated[product.id] = 1;
            }
          });
          return updated;
        });
      }

      return next;
    });
  }

  function handleUnselectProduct(productOrProducts: IProduct | IProduct[]) {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];

    setProductsToAdd(prev => {
      let next = [...prev];

      products.forEach(incoming => {
        const index = next.findIndex(product => product.id === incoming.id);
        if (index === -1) {
          return;
        }

        const removeIds = new Set((incoming.variants ?? []).map(variant => variant.id));
        if (removeIds.size === 0) {
          next = next.filter(product => product.id !== incoming.id);
          return;
        }

        const remainingVariants = (next[index].variants ?? []).filter(variant => !removeIds.has(variant.id));
        if (remainingVariants.length === 0) {
          next = next.filter(product => product.id !== incoming.id);
        } else {
          next[index] = { ...next[index], variants: remainingVariants, variant_count: remainingVariants.length };
        }
      });

      const remainingIds = new Set(next.map(product => product.id));
      setProductQuantities(quantities => {
        const updated = { ...quantities };
        Object.keys(updated).forEach(id => {
          if (!remainingIds.has(Number(id))) {
            delete updated[Number(id)];
          }
        });
        return updated;
      });

      return next;
    });
  }

  function handleQuantityChange(productId: number, quantity: number) {
    setProductQuantities(prev => ({ ...prev, [productId]: Math.max(1, quantity) }));
  }

  function resetPicker() {
    setSearch("");
    setBrowseFilter(defaultBrowseFilter);
    setProductsToAdd([]);
    setProductQuantities({});
    setProductSearchResults(undefined);
    setProductSearchTotalCount(undefined);
    setProductSearchPending(false);
    setProductSearchLoadingMore(false);
    setProductSearchSelectAllPending(false);
  }

  function openPicker(seed?: { products: IProduct[]; quantities: Record<number, number> }) {
    resetPicker();
    if (seed?.products?.length) {
      setProductsToAdd(seed.products);
      setProductQuantities(seed.quantities ?? {});
    }
  }

  return {
    search,
    setSearch,
    browseFilter,
    setBrowseFilter,
    productsToAdd,
    productQuantities,
    productSearchResults,
    productSearchTotalCount,
    productSearchPending,
    productSearchLoadingMore,
    productSearchSelectAllPending,
    departments,
    departmentsLoading,
    uncategorizedCount,
    loadMoreProducts,
    selectAllMatchingProducts,
    handleSelectProduct,
    handleUnselectProduct,
    handleQuantityChange,
    resetPicker,
    openPicker,
  };
}
